<template>
	<div>
		<PageHeader
			:data="{
				name: 'Subscribers',
				breadcrumbs: [
					{ name: 'Home', to: '/' },
					{ name: 'Clients', to: '/clients' },
					{ name: client.name, to: '/clients/edit/' + client.id },
				],
			}"
		/>
		<div class="actions d-flex justify-content-between mb-3">
			<input
				type="text"
				class="form-control w-25"
				v-model="request.search"
				placeholder="enter name, email or phone"
				@change="searchSubscribers()"
			/>
			<button @click="fetchSubscribers()" class="btn btn-primary btn-sm">Fetch Subscribers</button>
		</div>
		<div v-if="!loading">
			<table class="table table-hover">
				<thead>
					<tr>
						<th>Subscriber</th>
						<th>Notifications</th>
						<th>Subscriptions</th>
						<th>Last email</th>
						<th>Last sms</th>
						<th>Notification logs</th>
					</tr>
				</thead>
				<tbody v-if="subscribers.length > 0 && !loading">
					<tr v-for="subscriber in subscribers" :key="subscriber.id" class="bg-white">
						<td class="align-middle">
							<div>
								{{ subscriber.name }}
								<p class="m-0 p-0 text-secondary" style="font-size:11px">📧 {{ subscriber.email }}</p>
								<p class="m-0 p-0 text-secondary" style="font-size:11px">
									<template v-if="subscriber.phone">📱 {{ subscriber.phone }}</template>
									<template v-else>📱 Not provided</template>
								</p>
								<p>{{ subscriber.wordpress_id }}</p>
							</div>
						</td>

						<td class="align-middle">
							<span v-if="subscriber.email_notifications" class="badge bg-success me-1 p-1 px-2"
								>Email</span
							>
							<span v-if="subscriber.sms_notifications" class="badge bg-success p-1 px-2">SMS</span>
						</td>
						<td class="align-middle">
							<div
								class="subscriptions-post-categories"
								v-if="subscriber.subscriptions && subscriber.subscriptions.posts_categories.length > 0"
							>
								PC
								<small
									class="badge bg-primary rounded-1 me-1 px-1 py-1"
									v-for="item in subscriber.subscriptions.posts_categories"
									:key="item"
								>
									{{ item }}
								</small>
							</div>
							<div
								class="subscriptions-meeting-categories"
								v-if="
									subscriber.subscriptions && subscriber.subscriptions.meeting_categories.length > 0
								"
							>
								MC
								<small
									class="badge bg-primary rounded-1 me-1 px-1 py-1"
									v-for="item in subscriber.subscriptions.meeting_categories"
									:key="item"
								>
									{{ item }}
								</small>
							</div>
							<div
								class="subscriptions-meeting-documents"
								v-if="subscriber.subscriptions && subscriber.subscriptions.meeting_documents.length > 0"
							>
								MD
								<small
									class="badge bg-primary rounded-1 me-1 px-1 py-1"
									v-for="item in subscriber.subscriptions.meeting_documents"
									:key="item"
								>
									{{ item }}
								</small>
							</div>
						</td>
						<td class="align-middle">
							<div v-if="subscriber.last_email_status">
								<p class="p-0 m-0" v-if="!subscriber.last_email_post_id">
									Post ID: {{ subscriber.last_email_post_id }}
								</p>
								<p class="p-0 m-0" v-if="subscriber.last_email_at">📅 {{ subscriber.last_email_at }}</p>
								<p class="p-0 m-0" v-if="subscriber.last_email_status">
									<span v-if="subscriber.last_email_status.includes('Success')" class="text-success"
										>👌 {{ subscriber.last_email_status }}</span
									>
									<span v-if="!subscriber.last_email_status.includes('Success')" class="text-danger">
										⚠️ {{ subscriber.last_email_status }}</span
									>
								</p>
							</div>
							<div v-else>
								No Email-s sent to this subscriber
							</div>
						</td>
						<td class="align-middle">
							<p class="p-0 m-0" v-if="subscriber.last_sms_post_id">
								Post ID: {{ subscriber.last_sms_post_id }}
							</p>
							<div v-if="subscriber.last_sms_status">
								<p class="p-0 m-0" v-if="subscriber.last_sms_at">📅 {{ subscriber.last_sms_at }}</p>
								<div v-html="subscriber.last_sms_status"></div>
							</div>
							<div v-else>
								No SMS sent to this subscriber
							</div>
						</td>
						<td class="align-middle">
							<button class="btn btn-primary" @click="openSubscriberNotificationsLogModal(subscriber); subscriberSelected = subscriber;">
								<font-awesome-icon :icon="['fas', 'clock']" />
							</button>
						</td>
					</tr>
				</tbody>
				<tbody v-if="subscribers.length === 0 && !loading">
					<tr>
						<td colspan="5" class="text-center">
							<span class="fw-normal">There is not subscribers for this client</span>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<div v-else class="text-center d-flex justify-content-center align-items-center">
			<div class="spinner-grow spinner-grow-sm me-2" role="status">
				<span class="visually-hidden">Loading...</span>
			</div>
			Loading client subscribers
		</div>
		<!-- Pagination -->
		<div v-if="pagination" class="d-flex justify-content-between align-items-center">
			<!-- Items Statistic -->
			<div class="items-statistic">
				From
				{{ pagination.from }}
				to
				{{ pagination.to }}
				of
				{{ pagination.total }}
			</div>
			<!-- Pages navigation -->
			<div v-if="pagination.last_page > 1">
				<nav>
					<ul class="pagination" v-if="pagination.links && pagination.links.length > 0">
						<li
							:key="'pag-' + index"
							v-for="(link, index) in pagination.links"
							class="page-item"
							:class="link.active ? 'active' : ''"
						>
							<a role="button" :disabled="true" class="page-link" @click="paginate(link.url)">{{
								link.label | removeString
							}}</a>
						</li>
					</ul>
				</nav>
			</div>
		</div>
		<SubscriberNotificationsLogModal ref="SubscriberNotificationsLogModal" />
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import { mapState } from 'vuex'
import { handleErrors } from '../../actions/helpers'
import { Tooltip } from 'bootstrap'
import restApi from '../../api/index.js'
import SubscriberNotificationsLogModal from '@/components/clients/modals/SubscriberNotificationsLogModal.vue'
export default {
	name: 'ClientsSubscribers',
	components: { PageHeader, SubscriberNotificationsLogModal },
	data() {
		return {
			loading: false,
			client: {
				name: '',
			},
			subscribers: [],
			subscriberSelected: null,
			pagination: null,
			request: {
				page: this.$route.query.page | 1,
				client_id: this.$route.params.id,
				search: '',
			},
		}
	},
	computed: {
		...mapState(['authUser', 'storageUrl']),
	},
	created() {
		setTimeout(() => {
			document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
				new Tooltip(el)
			})
		}, 1100)
		this.getClient()
		this.getSubscribers()
	},
	methods: {
		// Get Client
		getClient() {
			restApi
				.get(`/clients/${this.$route.params.id}`)
				.then(({ data }) => {
					this.client = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Get Client
		getSubscribers() {
			this.loading = true
			restApi
				.get(`/tw/subscribers`, { params: this.request })
				.then(({ data }) => {
					this.subscribers = data.data
					this.subscribers.forEach(subscriber => {
						subscriber.subscriptions = JSON.parse(subscriber.subscriptions)
					})

					this.pagination = data
					delete this.pagination.data

					if (!this.compareRequest()) {
						this.$router.push({ path: this.$router.currentPath, query: this.request })
					}
					this.loading = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					this.loading = false
				})
		},

		// Compare request to avoid redirect to same page
		compareRequest() {
			let isEqual = true
			for (const property in this.request) {
				if (this.$route.query[property] != this.request[property]) {
					isEqual = false
				}
			}
			return isEqual
		},

		searchSubscribers() {
			this.getSubscribers()
		},

		// Paginate - when click on paginate buttons
		paginate(url) {
			let page = 1
			page = url.split('?')[1].replace('page=', '')
			this.request.page = page
			this.getSubscribers()
		},

		fetchSubscribers() {
			this.loading = true
			restApi
				.get(`/tw/subscribers/fetch/${this.client.id}`)
				.then(() => {
					this.getSubscribers()
					this.loading = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					this.loading = false
				})
		},
		openSubscriberNotificationsLogModal(subscriber) {
            this.$refs.SubscriberNotificationsLogModal.showModal(true, subscriber);
        },
	},
	filters: {
		removeString: function(value) {
			if (!value) return ''
			value = value.replace('&laquo;', '')
			return value.replace('&raquo;', '')
		},
	},
}
</script>
